import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NFeSaidaTagService } from '@shared/services/apis/repo-dfe/nfe-saida-tag.service';
import { AcaoTagsNFeSaidaEnum } from '@shared/enums/repo-dfe/acao-tags-nfe-saida.enum';
import { TagNFeSaidaResumoStruct } from '@shared/services/structs/repo-dfe/tag-nfe-saida-resumo.struct';
import { NFeSaidaAlterarTagsRequest } from '@shared/services/requests/repo-dfe/nfe-saida-alterar-tags.request';
import { AlterarTagsNotaFiscalSaidaDialogData } from './alterar-tags-nota-fiscal-saida-dialog-data';

@Component({
  selector: 'app-alterar-tags-nota-fiscal-saida-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, MatSelectModule],
  templateUrl: './alterar-tags-nota-fiscal-saida-dialog.component.html',
  styleUrls: ['./alterar-tags-nota-fiscal-saida-dialog.component.css'],
})
export class AlterarTagsNotaFiscalSaidaDialogComponent implements OnInit {
  public readonly acaoTags = AcaoTagsNFeSaidaEnum;
  public modo = AcaoTagsNFeSaidaEnum.Incluir;
  public pesquisa = '';
  public tagsFiltradas: TagNFeSaidaResumoStruct[] = [];
  public tagsSelecionadas = new Set<number>();
  public isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<AlterarTagsNotaFiscalSaidaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlterarTagsNotaFiscalSaidaDialogData,
    private nfeSaidaTagService: NFeSaidaTagService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.tagsFiltradas = [...this.data.tags];
    this.tagsSelecionadas = new Set(this.data.idsTagsAtuais);
  }

  get podeAplicar(): boolean { return !this.isLoading && this.tagsSelecionadas.size > 0; }

  public filtrarTags(): void {
    const pesquisa = this.pesquisa.trim().toLocaleLowerCase();
    this.tagsFiltradas = pesquisa ? this.data.tags.filter(tag => tag.descricao.toLocaleLowerCase().includes(pesquisa)) : [...this.data.tags];
  }

  public alternarTag(idTag: number): void {
    if (this.tagsSelecionadas.has(idTag)) this.tagsSelecionadas.delete(idTag);
    else this.tagsSelecionadas.add(idTag);
  }

  public alterarModo(): void { this.tagsSelecionadas.clear(); }

  public isTagSelecionada(idTagNFeSaida: number): boolean {
    return this.tagsSelecionadas.has(idTagNFeSaida);
  }

  public get contadorText(): string {
    const quantidade = this.tagsSelecionadas.size;
    return quantidade === 1 ? '1 tag selecionada' : `${quantidade} tags selecionadas`;
  }

  public get helpText(): string {
    return this.modo === AcaoTagsNFeSaidaEnum.Incluir
      ? 'As tags selecionadas serão adicionadas, mantendo as existentes.'
      : 'As tags selecionadas serão removidas, mantendo as demais existentes.';
  }

  public salvar(): void {
    if (!this.podeAplicar) return;
    this.isLoading = true;
    const request: NFeSaidaAlterarTagsRequest = {
      idsNFeSaida: this.data.idsNFeSaida,
      idsTags: [...this.tagsSelecionadas],
      acao: this.modo,
    };
    this.nfeSaidaTagService.alterarTags(request).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) { this.alertService.show('Erro', response.errorDescription, AlertType.error); return; }
        const mensagem = this.modo === AcaoTagsNFeSaidaEnum.Incluir ? 'Tags incluídas com sucesso!' : 'Tags removidas com sucesso!';
        this.alertService.show('Sucesso', mensagem, AlertType.success);
        this.dialogRef.close({ success: true });
      },
      error: () => { this.isLoading = false; this.alertService.show('Erro', 'Não foi possível atualizar as tags.', AlertType.error); },
    });
  }

  public fechar(): void { this.dialogRef.close(); }
}
